import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

const AUTHOR_ROLES = ["advisor", "accelerator", "investor", "provider", "admin"];

export default function FrameworkImport() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [template, setTemplate] = useState("");
  const [text, setText] = useState("");
  const [visibility, setVisibility] = useState("private");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/frameworks/template?lang=${lang}`)
      .then((r) => setTemplate(r.data.text || ""))
      .catch(() => setTemplate(""));
  }, [lang]);

  if (!user) return <div className="tb-loading">{t("common.loading")}</div>;
  if (!AUTHOR_ROLES.includes(user.role)) {
    return (
      <div className="tb-page">
        <h1 className="tb-h1">{t("fwImport.title")}</h1>
        <p className="tb-muted">{t("fwBuilder.roleNotAllowed")}</p>
      </div>
    );
  }

  const copyTemplate = async () => {
    try { await navigator.clipboard.writeText(template); } catch { /* noop */ }
  };
  const downloadTemplate = () => {
    const blob = new Blob([template], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tractionboard-framework-template-${lang}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (!text.trim()) { setErr(t("fwImport.empty")); return; }
    setBusy(true); setErr("");
    try {
      const r = await api.post("/frameworks/import", {
        text,
        source_lang: lang,
        visibility,
      });
      navigate(`/app/frameworks/build/${r.data.id}`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="tb-page" data-testid="fw-import">
      <Link to="/app/frameworks" className="tb-muted" style={{ fontSize: "0.85rem" }}>← {t("common.back")}</Link>
      <h1 className="tb-h1" style={{ marginTop: 8 }}>{t("fwImport.title")}</h1>
      <p className="tb-muted" style={{ maxWidth: 760, marginBottom: 28 }}>{t("fwImport.subtitle")}</p>

      {err && <div className="tb-form-error" style={{ marginBottom: 12 }} data-testid="fw-import-err">{err}</div>}

      <div className="tb-block">
        <h3 style={{ marginTop: 0 }}>{t("fwImport.step1Title")}</h3>
        <p className="tb-muted" style={{ marginTop: 0, marginBottom: 20 }}>{t("fwImport.step1Hint")}</p>
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          <button type="button" className="tb-ghost-btn" onClick={copyTemplate} data-testid="fw-import-copy">{t("fwImport.copy")}</button>
          <button type="button" className="tb-ghost-btn" onClick={downloadTemplate} data-testid="fw-import-download">{t("fwImport.download")}</button>
        </div>
        <textarea
          readOnly
          value={template}
          rows={16}
          data-testid="fw-import-template"
          style={{
            width: "100%",
            fontFamily: "var(--font-mono, ui-monospace, monospace)",
            fontSize: "0.82rem",
            lineHeight: "1.7",
            padding: "16px 18px",
            background: "rgba(0,0,0,0.25)",
            whiteSpace: "pre-wrap",
          }}
        />
      </div>

      <div className="tb-block" style={{ marginTop: 14 }}>
        <h3 style={{ marginTop: 0 }}>{t("fwImport.step2Title")}</h3>
        <p className="tb-muted" style={{ marginTop: 0, marginBottom: 20 }}>{t("fwImport.step2Hint")}</p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={20}
          placeholder={t("fwImport.pastePlaceholder")}
          data-testid="fw-import-text"
          style={{
            width: "100%",
            fontFamily: "var(--font-mono, ui-monospace, monospace)",
            fontSize: "0.88rem",
            lineHeight: "1.7",
            padding: "16px 18px",
          }}
        />
        <div style={{ marginTop: 10 }}>
          <label>{t("fwBuilder.visibility")}</label>
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} data-testid="fw-import-visibility">
            <option value="private">{t("fwBuilder.visPrivate")}</option>
            <option value="connections">{t("fwBuilder.visConnections")}</option>
            <option value="public">{t("fwBuilder.visPublic")}</option>
          </select>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
        <button type="button" className="tb-primary-btn" onClick={submit} disabled={busy} data-testid="fw-import-submit">
          {busy ? t("fwImport.importing") : t("fwImport.importCta")}
        </button>
        <Link to="/app/frameworks" className="tb-ghost-btn" style={{ textDecoration: "none" }}>{t("book.cancel")}</Link>
      </div>
    </div>
  );
}
